import { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React from 'react';
import Link from '@/components/common/Link';
import { replaceBackendWithFrontendUrl } from '@/utils/misc';

const SideMenu = observer(({
  menuData,
  selectedPage,
}) => {
  const scrollContainerRef = useRef(null);
  const menuButtonRefs = useRef({});

  // Scroll active item into view on mobile
  useEffect(() => {
    const timerId = setTimeout(() => {
      const activeItem = menuData?.items?.find(item => selectedPage === replaceBackendWithFrontendUrl(item.url));
      const activeRef = activeItem ? menuButtonRefs.current[activeItem.ID] : null;
      const containerRef = scrollContainerRef.current;

      if (activeRef && containerRef) {
        activeRef.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        });
      }
    }, 0);

    return () => clearTimeout(timerId);
  }, [selectedPage, menuData?.items]);

  const hasMenuItems = menuData?.items?.length > 0;

  if (!hasMenuItems) return null;

  return (
    <>
      {/* --- Mobile View --- */}
      <div className="md:hidden">
        <AnimatePresence>
          <motion.div
            initial={{ y: -100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -100, opacity: 0 }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="sticky top-20 left-0 right-0 z-40 bg-white shadow-md"
          >
            <div ref={scrollContainerRef} className="overflow-x-auto mask-l-from-80% mask-l-to-100%">
              <div className="flex items-center whitespace-nowrap p-2 space-x-2">
                {menuData.items.map((item) => {
                  const itemUrl = replaceBackendWithFrontendUrl(item.url);
                  const isActive = selectedPage === itemUrl;
                  return (
                    <div key={item.ID} className="flex items-center px-1">
                      <Link
                        ref={(el) => {
                          if (el) menuButtonRefs.current[item.ID] = el;
                          else delete menuButtonRefs.current[item.ID];
                        }}
                        href={itemUrl}
                        className={`p-1 text-primary-studio font-secondary hover:bg-gray-300 text-start ${
                          isActive ? 'bg-primary-light-studio' : ''
                        }`}
                      >
                        {item.title}
                      </Link>
                    </div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* --- Desktop View --- */}
      <aside className="hidden md:block sm:w-[23%] lg:w-[17%] h-screen sticky top-28 lg:top-36 pe-0 sm:pe-6 md:pe-2 lg:pe-5 2xl:pe-10 sm:ps-3 lg:ps-0">
        <nav>
          <ul className="space-y-2">
            {menuData.items.map((item) => {
              const itemUrl = replaceBackendWithFrontendUrl(item.url);
              const isActive = selectedPage === itemUrl;
              return (
                <li key={item.ID}>
                  <Link
                    href={itemUrl}
                    className={`px-2 pb-1 w-full text-xl text-primary-studio font-secondary hover:bg-gray-300 text-start flex items-center ${isActive ? 'bg-gray-300' : ''}`}
                  >
                    {item.title}
                  </Link>
                  <hr className="dotted-spaced mt-2" />
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>
    </>
  );
});

SideMenu.propTypes = {
  menuData: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        ID: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        title: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
    ),
  }),
  selectedPage: PropTypes.string,
};

export default SideMenu;